import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { Operator, OperatorRole } from '@/schemas/users';

const ROLES: OperatorRole[] = ['admin', 'developer_operator', 'manufacturer_operator'];

interface ChangeRoleDialogProps {
  user: Operator | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (role: OperatorRole) => void;
  isLoading?: boolean;
}

export default function ChangeRoleDialog({
  user,
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: ChangeRoleDialogProps) {
  const { t } = useTranslation();
  const [role, setRole] = React.useState<OperatorRole>('developer_operator');

  React.useEffect(() => {
    if (open && user) setRole(user.role);
  }, [open, user]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('operators.roleDialog.title')}</DialogTitle>
          <DialogDescription asChild>
            <div>
              <Trans
                i18nKey="operators.roleDialog.description"
                values={{ username: user.username }}
                components={{ strong: <strong /> }}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="change-role">{t('operators.roleDialog.role')}</Label>
          <Select value={role} onValueChange={(v) => setRole(v as OperatorRole)}>
            <SelectTrigger id="change-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(`operators.roles.${r}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSubmit(role)} disabled={isLoading || role === user.role}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('operators.roleDialog.saving')}
              </>
            ) : (
              t('operators.roleDialog.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
